import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { KitFontId, ReleaseThemeId, themeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { ThemeStill } from '../../themes/ThemeStill';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { ThemeStillSong } from './useThemeStillSong';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeLivePreviewProps {
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  palette: readonly string[];
  song: ThemeStillSong;
}

/**
 * "Your song in <Theme>" (design q2, q4): a still of the user's own song in the colours of its
 * cover shaped by the theme and in the type of the look, and the 4 theme colours. The colours are
 * computed here with the same code as the server (`themeColours`), so the still changes at once,
 * before the PATCH ends.
 */
const ThemeLivePreviewComponent: React.FC<ThemeLivePreviewProps> = ({ themeId, fontOverride, palette, song }) => {
  const intl = useIntl();
  const colours = useMemo(() => themeColours(themeId, palette), [palette, themeId]);
  const themeName = intl.formatMessage(releaseThemeMessages[themeId].name);
  return (
    <div className={styles.live}>
      <span className={styles.liveCaption}>{intl.formatMessage(themeMessages.liveCaption, { theme: themeName })}</span>
      <ThemeStill
        className={styles.liveStill}
        themeId={themeId}
        fontOverride={fontOverride}
        palette={palette}
        artworkUrl={song.artworkUrl}
        title={song.title}
        artist={song.artist}
        kicker={song.kicker}
        testId="theme-live-still"
      />
      <span className={styles.note}>{intl.formatMessage(themeMessages.liveNote)}</span>
      <div className={styles.colours} data-testid="theme-colours">
        {[colours.background, colours.surface, colours.text, colours.accent].map((colour, index) => (
          // The 4 slots can share a colour, so the slot index keeps the key unique.
          // eslint-disable-next-line react/no-array-index-key
          <i key={index} className={styles.colour} style={{ background: colour }} title={colour} />
        ))}
      </div>
    </div>
  );
};

export const ThemeLivePreview = TypedMemo(ThemeLivePreviewComponent);
